import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HubAction } from 'enums/HubAction';
import { MessageType } from 'enums/MessageType';
import { environment } from 'environments/environment';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { LedgerPostModel } from 'models/generated/Entities.Accounting.LedgerPost';
import { PlanReplacementModel } from 'models/generated/Entities.GormCrm.PlanReplacement';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { ProductPlanSettingModel } from 'models/generated/Entities.GormCrm.ProductPlanSetting';
import { SettlementModel } from 'models/generated/Entities.GormCrm.Settlement';
import { SupersedableProductPlanDtoModel } from 'models/generated/GormGateway.Models.SupersedableProductPlanDto';
import { FreeProductPlanSettingDtoModel } from 'models/generated/Models.FreeProductPlanSettingDto';
import { ProductPlanPreparationRequestModel } from 'models/generated/Models.ProductPlanPreparationRequest';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { firstValueFrom } from 'rxjs';
import { AccountingService } from 'services/accounting.service';
import { LiveDataTracker, TrackingContext } from 'services/data-tracking';
import { IPushMessage, PushService } from 'services/push.service';
import { SettlementService } from 'services/settlement.service';

@Injectable()
export class ProductPlanService {

	private productPlanUrl = `${environment.gatewayUrl}/api/product-plans`;
	private gatewayUrl = `${environment.gatewayUrl}/api/gateway/product-plans`;
	private standoutSyncUrl = `${environment.gatewayUrl}/api/sync`;
	private productPlanTracker = new LiveDataTracker<ProductPlanModel, { productPlanId: string, companyId?: string }>(plan => plan.id!);

	constructor(
		private readonly http: HttpClient, 
		private readonly pushService: PushService, 
		private readonly settlementService: SettlementService,
		private readonly accountingService: AccountingService,
		private readonly datePipe: CustomDatePipe) {

		this.pushService.onChanges<{ companyId: string, productPlanId: string }>(MessageType.AccountingPlanChanges).subscribe(event => {
			if (event.action !== HubAction.Insert || !event.data) return;
			this.refreshProductPlan(event, event.data.productPlanId, true);
		});
		this.pushService.onProductPlanChanges.subscribe(event => {
			if (event.action == HubAction.Insert) return; // We don't need to refresh the data when a new plan is created, as it will be added to the list of plans when AccountPlan is created
			this.refreshProductPlan(event, event.data.productPlanId);
		});
	}

	private refreshProductPlan(event: IPushMessage<{ companyId: string, productPlanId: string }>, productPlanId: string, addCallback: boolean = false): void {
		this.productPlanTracker.refreshData(event, productPlanId, () => this.getProductPlan(productPlanId), addCallback ? async (plan, trackingContext) => {
			const accountingPlans = await this.accountingService.getAllAccountingPlansFromProductPlanIds([plan.id!], trackingContext);
			plan.accountingPlans = accountingPlans.filter(ap => ap.productPlanId == plan.id);
			accountingPlans.forEach((ap) => ap.productPlan = plan);
		} : undefined);
	}

	getAllProductPlansAndAccountingPlansFromCompany(companyId: string, trackingContext?: TrackingContext): Promise<ProductPlanModel[]> {
		return this.getProductPlansByCompanyId(companyId, trackingContext).then(productPlans => {
			return this.accountingService.getAllAccountingPlansFromProductPlanIds(productPlans.map(pp => pp.id!), trackingContext).then(accountingPlans => {
				productPlans.forEach((pp) => pp.accountingPlans = accountingPlans.filter(ap => ap.productPlanId == pp.id));
				accountingPlans.forEach((ap) => ap.productPlan = productPlans.find(pp => pp.id == ap.productPlanId));
				return productPlans;
			});
		});
	}
	
	getAllProductPlansAndAccountingPlansForSettlement(settlement: SettlementModel): Promise<SettlementModel>{
		return this.getAllProductPlansAndAccountingPlansFromCompany(settlement.companyId!).then(plans => {
			settlement.settlementProductPlans.forEach(spp => {
				spp.productPlan = plans.find(pp => pp.id === spp.productPlanId);
				if (spp.productPlan) spp.productPlan.accountingPlan = this.accountingService.filterActiveAccountingPlan(spp.productPlan.accountingPlans);
			});
			return settlement;
		});
	}

	getProductPlansByCompanyId(companyId: string, trackingContext?: TrackingContext): Promise<ProductPlanModel[]> {
		companyId = companyId.toLowerCase();
		return firstValueFrom(this.http.get<ProductPlanModel[]>(`${this.productPlanUrl}/company/${companyId}`))
			.then(productPlans => this.productPlanTracker.trackArrayForComponent(trackingContext, productPlans, eventData => eventData.companyId === companyId));
	}
	filterProductPlansByTag(productPlans: ProductPlanModel[], tag: string): ProductPlanModel[] {
		return productPlans.filter(plan => this.productPlanHasTag(plan, tag));
	}
	productPlanHasTag(productPlan: ProductPlanModel | undefined, tag: string): boolean {
		return productPlan?.productPlanTags?.some(ppt => ppt.tag === tag) ?? false;
	}

	getProductPlan(productPlanId: string, trackingContext?: TrackingContext): Promise<ProductPlanModel> {
		return this.getProductPlans([productPlanId], trackingContext).then(plans => plans[0]);
	}

	getProductPlans(productPlanIds: string[], trackingContext?: TrackingContext): Promise<ProductPlanModel[]> {
		return firstValueFrom(this.http.post<ProductPlanModel[]>(`${this.productPlanUrl}/list`, productPlanIds))
			.then(plans => this.productPlanTracker.trackArrayForComponent(trackingContext, plans, eventData => productPlanIds.includes(eventData.productPlanId)));
	}

	getAllProductPlanTags(): Promise<string[]> {
		return firstValueFrom(this.http.get<string[]>(`${this.productPlanUrl}/tags`));
	}

	getCompanyPlanReplacements(companyId: string): Promise<PlanReplacementModel[]> {
		return firstValueFrom(this.http.get<PlanReplacementModel[]>(`${this.productPlanUrl}/replacements/company/${companyId}`)); 
	}
	
	getFreeProductPlanSettings(destinationTag: string): Promise<FreeProductPlanSettingDtoModel[]> {
		return firstValueFrom(this.http.get<FreeProductPlanSettingDtoModel[]>(`${this.productPlanUrl}/free-product-plan-settings?destinationTag=${destinationTag}`));
	}

	getProductPlanSettings(): Promise<ProductPlanSettingModel[]> {
		return firstValueFrom(this.http.get<ProductPlanSettingModel[]>(`${this.productPlanUrl}/settings`));
	}

	upsertProductPlanSetting(request: ProductPlanSettingModel): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.productPlanUrl}/setting`, request));
	}

	deleteProductPlanSetting(id: string): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.productPlanUrl}/setting/${id}`));
	}

	createProductPlan(productPlanCreateRequest: ProductPlanPreparationRequestModel): Promise<ProductPlanModel> {
		productPlanCreateRequest.include(request => request.productPlan);
		return firstValueFrom(this.http.post<ProductPlanModel>(this.productPlanUrl, productPlanCreateRequest));
	}

	updateProductPlanName(productPlanId: string, updatedProductPlanName: string, resetIdentification: boolean = false): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.productPlanUrl}/${productPlanId}/name`, { name: updatedProductPlanName, resetIdentification }));
	}
	updateProductPlanPaymentScheme(productPlanId: string, paymentScheme: number): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.productPlanUrl}/${productPlanId}/payment-scheme?paymentScheme=${paymentScheme}`, { }));
	}
	updateProductPlanParent(productPlanId: string, newParentId?: string): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.productPlanUrl}/${productPlanId}/parent`, { parentId: newParentId }));
	}
	updateProductPlanTerminationNoticePeriod(productPlanId: string, period: number): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.productPlanUrl}/${productPlanId}/termination-notice-period?period=${period}`, {}));
	}

	setProductPlanNextPeriod(productPlanId: string, nextPeriod: number): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.productPlanUrl}/${productPlanId}/next-period`, { nextPeriod: nextPeriod }));
	}

	setProductPlanAnnualValueForRenewal(productPlanId: string, annualValueForRenewal: number): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.productPlanUrl}/${productPlanId}/annual-value-for-renewal`, { annualValueForRenewal: annualValueForRenewal }));
	}

	setProductPlanTerminationDate(productPlanId: string, terminationDate: Date, endDate: Date): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.productPlanUrl}/${productPlanId}/termination-date`, { terminationDate: terminationDate, endDate: endDate }));
	}
	setProductPlanRestrictEndDate(productPlanId: string, restrictEndDate?: Date, ignoreWarning: boolean = false): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.productPlanUrl}/${productPlanId}/restrict-end-date?restrictEndDate=${restrictEndDate?.toISOString()}&ignoreWarning=${ignoreWarning}`, {}));
	}
	setProductPlanTags(productPlanId: string, tags: string[]): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.productPlanUrl}/${productPlanId}/tags`, tags));
	}

	undoTerminateProductPlan(productPlanId: string, endDate?: Date): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.productPlanUrl}/${productPlanId}/undo-termination-date`, {endDate: endDate}));
	}

	getSupersedableProductPlans(companyId: string): Promise<SupersedableProductPlanDtoModel[]> {
		return firstValueFrom(this.http.get<SupersedableProductPlanDtoModel[]>(`${this.gatewayUrl}/supersedable-productplans`, { params: { companyId, includeSuspended: true } }));
	}

	async planIsPartOfActiveSettlement(productPlan: ProductPlanModel): Promise<boolean> {		
		if (productPlan.settlementProductPlans && productPlan.settlementProductPlans.length > 0 && !productPlan.settlementProductPlans.some(spp => spp.settlement === undefined)) {
			return productPlan.settlementProductPlans.some(settlementProductPlan => settlementProductPlan.settlement!.isActive);
		}
		const settlements = await this.settlementService.getSettlementsByProductPlanId(productPlan.id!);
		return settlements.filter(s => s.isActive).length > 0;
	}

	showPlan(productPlan: ProductPlanModel, activeAccountingPlans: Map<ProductPlanModel, AccountingPlanModel>, ledgerPosts: LedgerPostModel[], showInactivePlans: boolean, rootLevel: boolean = false) {
		if (rootLevel && productPlan.parentId) return false;
		if (showInactivePlans) return true;

		let accountingPlan = activeAccountingPlans.get(productPlan);
		// if active, show plan
		if (this.accountingService.isActive(accountingPlan))
			return true;
		// if has unregistered ledgerposts, show plan
		if (ledgerPosts.some(lp => accountingPlan?.id == lp.accountingPlanId && (!lp.isRegistered || (lp.ledgerHeader && !lp.ledgerHeader!.closed))))
			return true;
		// if has children that are shown, show plan
		if (productPlan.inverseParent.find(plan => this.showPlan(plan, activeAccountingPlans, ledgerPosts, showInactivePlans, false)))
			return true;

		return false;
	}

	planIsNotChildOf(plan: ProductPlanModel, checkForId: string): boolean {
		return plan.id !== checkForId && (!plan.parent || this.planIsNotChildOf(plan.parent, checkForId));
	}

	searchProductPlans(request: { expiresFrom?: Date, expiresTo?: Date, tags?: string[], isDelivered?: boolean, isTerminated?: boolean, includeCompanyData?: boolean, includeOrderLines?: boolean; }, trackingContext?: TrackingContext) {
		return firstValueFrom(this.http.get<ProductPlanModel[]>(`${this.productPlanUrl}/search`, {
			params: new HttpParams({
				fromObject: {
					tags: request.tags ?? [],
					includeCompanyData: request.includeCompanyData ?? false,
					includeOrderLines: request.includeOrderLines ?? false,
					expiresFrom: request.expiresFrom ? this.datePipe.toIso8601(request.expiresFrom) : '',
					expiresTo: request.expiresTo ? this.datePipe.toIso8601(request.expiresTo) : '',
					isDelivered: request.isDelivered ?? '',
					isTerminated: request.isTerminated ?? ''
				}
			})
		}))
		.then(productPlans => this.productPlanTracker.trackArrayForComponent(trackingContext, productPlans));
	}


	addProductPlansToStandoutSync(productPlanIds: string[]): Promise<void> {
		return firstValueFrom(this.http.post<void>(`${this.standoutSyncUrl}/add-sync`, productPlanIds));
	}
}
